import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, Mail, Phone, MapPin, Loader2, Globe, Instagram } from 'lucide-react';
import { submitContactForm } from '../services/api';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Home = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error(t('contact.form.requiredError'));
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await submitContactForm(formData);
      
      if (result.success) {
        toast.success(result.data.message || t('contact.form.success'));
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        toast.error(result.error || t('contact.form.error'));
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('contact.form.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const getLanguageLabel = () => {
    switch (i18n.language) {
      case 'es':
        return 'ES';
      case 'en':
        return 'EN';
      case 'de':
        return 'DE';
      default:
        return 'ES';
    }
  };

  return (
    <div className='min-h-screen bg-black text-white'>
      {/* Header Navigation */}
      <header className='fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-neon-yellow/20'>
        <nav className='container mx-auto px-6 py-4 flex items-center justify-between'>
          <div className='flex items-center'>
            <img src='/logo.png' alt='am DESIGN STUDIO' className='h-10 w-auto' />
          </div>
          <div className='flex items-center gap-6'>
            <div className='hidden md:flex items-center gap-8'>
              <a href='#services' className='nav-link'>{t('nav.services')}</a>
              <a href='#team' className='nav-link'>{t('nav.team')}</a>
              <Button onClick={scrollToContact} className='cta-button'>{t('nav.contact')}</Button>
            </div>
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' size='sm' className='cta-button-outline flex items-center gap-2'>
                  <Globe size={16} />
                  {getLanguageLabel()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='bg-zinc-900 border-neon-yellow/30'>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('es')}
                  className='text-white hover:bg-neon-yellow hover:text-black cursor-pointer'
                >
                  Español
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('en')}
                  className='text-white hover:bg-neon-yellow hover:text-black cursor-pointer'
                >
                  English
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('de')}
                  className='text-white hover:bg-neon-yellow hover:text-black cursor-pointer'
                >
                  Deutsch
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className='hero-section relative min-h-screen flex items-center justify-center overflow-hidden pt-20'>
        <div className='absolute inset-0 z-0'>
          <div className='absolute top-20 right-10 w-96 h-96 bg-neon-yellow/10 rounded-full blur-3xl animate-pulse-slow'></div>
          <div className='absolute bottom-20 left-10 w-80 h-80 bg-neon-yellow/5 rounded-full blur-3xl animate-pulse-slow' style={{ animationDelay: '1s' }}></div>
        </div>
        <div className='container mx-auto px-6 z-10 text-center'>
          <h1 className='hero-heading text-6xl md:text-8xl font-extrabold uppercase mb-8 leading-tight'>
            {t('hero.title')}
          </h1>
          <p className='text-lg md:text-xl mb-6 max-w-4xl mx-auto text-gray-300 leading-relaxed'>
            {t('hero.subtitle')}
          </p>
          <p className='text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-white font-semibold'>
            {t('hero.subtitle2')}
          </p>
          <div className='flex flex-col sm:flex-row gap-4 justify-center items-center'>
            <Button onClick={scrollToContact} className='cta-button-large group'>
              {t('hero.cta')}
              <ArrowRight className='ml-2 group-hover:translate-x-1 transition-transform' size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id='services' className='py-24 bg-black'>
        <div className='container mx-auto px-6'>
          <div className='text-center mb-16'>
            <h2 className='section-heading text-5xl md:text-6xl font-bold mb-4 uppercase'>
              {t('services.title')} <span className='text-neon-yellow'>{t('services.titleHighlight')}</span>
            </h2>
            <p className='text-xl text-gray-400 max-w-2xl mx-auto'>
              {t('services.subtitle')}
            </p>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {/* Branding */}
            <Card className='service-card p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 bg-neon-yellow text-black border-neon-yellow hover:shadow-neon-yellow/50'>
              <h3 className='text-2xl font-bold mb-4'>{t('services.branding.title')}</h3>
              <p className='text-black/80'>{t('services.branding.description')}</p>
            </Card>

            {/* Digital Campaigns */}
            <Card className='service-card p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 bg-black text-neon-yellow border-neon-yellow hover:shadow-neon-yellow/30'>
              <h3 className='text-2xl font-bold mb-4'>{t('services.campaigns.title')}</h3>
              <p className='text-gray-300'>{t('services.campaigns.description')}</p>
            </Card>

            {/* Social Media */}
            <Card className='service-card p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 bg-neon-yellow text-black border-neon-yellow hover:shadow-neon-yellow/50'>
              <h3 className='text-2xl font-bold mb-4'>{t('services.social.title')}</h3>
              <p className='text-black/80'>{t('services.social.description')}</p>
            </Card>

            {/* Graphic Design */}
            <Card className='service-card p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 bg-black text-neon-yellow border-neon-yellow hover:shadow-neon-yellow/30'>
              <h3 className='text-2xl font-bold mb-4'>{t('services.graphic.title')}</h3>
              <p className='text-gray-300'>{t('services.graphic.description')}</p>
            </Card>

            {/* Consulting */}
            <Card className='service-card p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 bg-neon-yellow text-black border-neon-yellow hover:shadow-neon-yellow/50'>
              <h3 className='text-2xl font-bold mb-4'>{t('services.consulting.title')}</h3>
              <p className='text-black/80'>{t('services.consulting.description')}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id='team' className='py-24 bg-black'>
        <div className='container mx-auto px-6'>
          <div className='text-center mb-16'>
            <h2 className='section-heading text-5xl md:text-6xl font-bold mb-4 uppercase'>
              {t('team.title')} <span className='text-neon-yellow'>{t('team.titleHighlight')}</span>
            </h2>
            <p className='text-xl text-gray-400 max-w-2xl mx-auto'>
              {t('team.subtitle')}
            </p>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto'>
            {['member1', 'member2', 'member3'].map((member, index) => (
              <Card key={index} className='team-card p-8 bg-zinc-900 border-2 border-neon-yellow/30 hover:border-neon-yellow hover:bg-neon-yellow/5 transition-all duration-300 text-center'>
                {member === 'member1' ? (
                  <div className='w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-neon-yellow/40'>
                    <img 
                      src='/andy-martignon.jpg' 
                      alt='Andy Martignon' 
                      className='w-full h-full object-cover'
                      style={{ objectPosition: 'center 35%' }}
                    />
                  </div>
                ) : (
                  <div className='w-24 h-24 bg-neon-yellow/20 rounded-full mx-auto mb-4 flex items-center justify-center'>
                    <span className='text-3xl font-bold text-neon-yellow'>{t(`team.${member}.name`).charAt(0)}</span>
                  </div>
                )}
                <h3 className='text-xl font-bold text-neon-yellow mb-2'>{t(`team.${member}.name`)}</h3>
                <p className='text-sm text-gray-300 mb-1'>{t(`team.${member}.role`)}</p>
                <p className='text-xs text-gray-500'>{t(`team.${member}.specialty`)}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id='contact' className='py-24 bg-zinc-950'>
        <div className='container mx-auto px-6'>
          <div className='max-w-5xl mx-auto'>
            <div className='text-center mb-16'>
              <h2 className='section-heading text-5xl md:text-6xl font-bold mb-4 uppercase'>
                {t('contact.title')} <span className='text-neon-yellow'>{t('contact.titleHighlight')}</span>?
              </h2>
              <p className='text-xl text-gray-400'>
                {t('contact.subtitle')}
              </p>
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
              {/* Contact Form */}
              <Card className='p-8 bg-zinc-900 border-2 border-neon-yellow/30'>
                <form onSubmit={handleSubmit} className='space-y-6'>
                  <div>
                    <label className='block text-sm font-semibold mb-2 text-neon-yellow'>{t('contact.form.name')}</label>
                    <Input 
                      type='text' 
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className='w-full bg-black border-neon-yellow/50 text-white focus:border-neon-yellow'
                      placeholder={t('contact.form.namePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-semibold mb-2 text-neon-yellow'>{t('contact.form.email')}</label>
                    <Input 
                      type='email' 
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className='w-full bg-black border-neon-yellow/50 text-white focus:border-neon-yellow'
                      placeholder={t('contact.form.emailPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-semibold mb-2 text-neon-yellow'>{t('contact.form.company')}</label>
                    <Input 
                      type='text' 
                      name='company'
                      value={formData.company}
                      onChange={handleInputChange}
                      className='w-full bg-black border-neon-yellow/50 text-white focus:border-neon-yellow'
                      placeholder={t('contact.form.companyPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-semibold mb-2 text-neon-yellow'>{t('contact.form.message')}</label>
                    <Textarea 
                      name='message'
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className='w-full bg-black border-neon-yellow/50 text-white focus:border-neon-yellow'
                      placeholder={t('contact.form.messagePlaceholder')}
                    />
                  </div>
                  <Button type='submit' className='w-full cta-button-large' disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className='mr-2 animate-spin' size={20} />
                        {t('contact.form.sending')}
                      </>
                    ) : (
                      t('contact.form.submit')
                    )}
                  </Button>
                </form>
              </Card>

              {/* Contact Info */}
              <div className='space-y-8'>
                <Card className='p-6 bg-neon-yellow text-black border-2 border-neon-yellow hover:shadow-neon-yellow/50 transition-all duration-300'>
                  <div className='flex items-start gap-4'>
                    <Mail className='mt-1' size={24} />
                    <div>
                      <h3 className='font-bold text-lg mb-1'>{t('contact.info.email')}</h3>
                      <p className='text-black/80'>hola@am-design.studio</p>
                    </div>
                  </div>
                </Card>
                <Card className='p-6 bg-neon-yellow text-black border-2 border-neon-yellow hover:shadow-neon-yellow/50 transition-all duration-300'>
                  <div className='flex items-start gap-4'>
                    <Instagram className='mt-1' size={24} />
                    <div>
                      <h3 className='font-bold text-lg mb-1'>{t('contact.info.instagram')}</h3>
                      <a href='https://instagram.com/by.am.de' target='_blank' rel='noopener noreferrer' className='text-black/80 hover:text-black hover:underline transition-all'>
                        @by.am.de
                      </a>
                    </div>
                  </div>
                </Card>
                <Card className='p-6 bg-neon-yellow text-black border-2 border-neon-yellow hover:shadow-neon-yellow/50 transition-all duration-300'>
                  <div className='flex items-start gap-4'>
                    <MapPin className='mt-1' size={24} />
                    <div>
                      <h3 className='font-bold text-lg mb-1'>{t('contact.info.location')}</h3>
                      <p className='text-black/80'>{t('contact.info.locationValue')}</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='bg-black border-t border-neon-yellow/20 py-12'>
        <div className='container mx-auto px-6'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mb-8'>
            <div>
              <img src='/logo.png' alt='am DESIGN STUDIO' className='h-12 w-auto mb-4' />
              <p className='text-gray-400'>
                {t('footer.tagline')}
              </p>
            </div>
            <div>
              <h4 className='text-lg font-semibold mb-4 text-neon-yellow'>{t('footer.servicesTitle')}</h4>
              <ul className='space-y-2 text-gray-400'>
                <li>{t('services.branding.title')}</li>
                <li>{t('services.campaigns.title')}</li>
                <li>{t('services.social.title')}</li>
                <li>{t('services.graphic.title')}</li>
                <li>{t('services.consulting.title')}</li>
              </ul>
            </div>
            <div>
              <h4 className='text-lg font-semibold mb-4 text-neon-yellow'>{t('footer.contactTitle')}</h4>
              <ul className='space-y-2 text-gray-400'>
                <li>hola@am-design.studio</li>
                <li>
                  <a href='https://instagram.com/by.am.de' target='_blank' rel='noopener noreferrer' className='hover:text-neon-yellow transition-colors'>
                    @by.am.de
                  </a>
                </li>
                <li>{t('contact.info.locationValue')}</li>
              </ul>
            </div>
          </div>
          <div className='border-t border-neon-yellow/20 pt-8 text-center text-gray-500'>
            <p>&copy; 2025 am DESIGN STUDIO. {t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
